import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFlight } from '../contexts/FlightContext';
import { PwaIcon } from '../types';
import { XCircleIcon, SpinnerIcon, CheckIcon, UploadIcon } from './Icons';

interface IconPreview {
    src: string;
    size: number;
    purpose: 'any' | 'maskable';
}

const IconWizard: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const { updatePwaIcons } = useAuth();
    const { showNotification } = useFlight();
    const [sourceImage, setSourceImage] = useState<string | null>(null);
    const [backgroundColor, setBackgroundColor] = useState<string>('#1e293b');
    const [generatedIcons, setGeneratedIcons] = useState<IconPreview[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setSourceImage(e.target?.result as string);
                setGeneratedIcons([]);
            };
            reader.readAsDataURL(file);
        } else {
            showNotification('Please select a valid image file.', 'error');
        }
    };

    const generateIcons = useCallback(async () => {
        if (!sourceImage || !canvasRef.current) return;
        setIsLoading(true);

        const img = new Image();
        img.src = sourceImage;
        await new Promise(resolve => { img.onload = resolve; });

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const iconConfigs = [
            { size: 192, purpose: 'any' as const },
            { size: 512, purpose: 'any' as const },
            { size: 192, purpose: 'maskable' as const },
            { size: 512, purpose: 'maskable' as const },
        ];

        const previews: IconPreview[] = [];

        for (const config of iconConfigs) {
            canvas.width = config.size;
            canvas.height = config.size;

            ctx.fillStyle = backgroundColor;
            ctx.fillRect(0, 0, config.size, config.size);

            if (config.purpose === 'maskable') {
                // For maskable icons, the image should be contained within a "safe zone"
                // which is a circle with a radius of 40% of the icon size.
                const padding = config.size * 0.1; // 10% padding on each side
                const imageSize = config.size - (padding * 2);
                ctx.drawImage(img, padding, padding, imageSize, imageSize);
            } else {
                ctx.drawImage(img, 0, 0, config.size, config.size);
            }
            previews.push({
                src: canvas.toDataURL('image/png'),
                size: config.size,
                purpose: config.purpose
            });
        }

        setGeneratedIcons(previews);
        setIsLoading(false);
    }, [sourceImage, backgroundColor]);

    const handleSave = async () => {
        if (generatedIcons.length === 0) {
            showNotification('Please generate icons before saving.', 'error');
            return;
        }
        setIsLoading(true);
        try {
            const pwaIcons: PwaIcon[] = generatedIcons.map(icon => ({
                src: icon.src,
                sizes: `${icon.size}x${icon.size}`,
                type: 'image/png',
                purpose: icon.purpose,
            }));
            await updatePwaIcons(pwaIcons);
            showNotification('App icons updated successfully! The changes will apply on the next app load.', 'success');
            onClose();
        } catch (error) {
            console.error('Failed to save PWA icons:', error);
            showNotification('An error occurred while saving the icons.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-slide-up-fade-in" role="dialog" aria-modal="true">
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div className="bg-slate-800 rounded-lg shadow-2xl w-full max-w-4xl h-full max-h-[90vh] flex flex-col border border-slate-700">
                <header className="flex-shrink-0 flex items-center justify-between p-4 border-b border-slate-700">
                    <h2 className="text-2xl font-bold text-white">App Icon & PWA Manifest Generator</h2>
                    <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors">
                        <XCircleIcon className="w-8 h-8" />
                    </button>
                </header>

                <main className="flex-grow p-6 overflow-y-auto flex flex-col md:flex-row gap-6">
                    {/* Step 1 & 2: Upload and Configure */}
                    <div className="w-full md:w-1/3 flex-shrink-0 space-y-6">
                        <div>
                            <h3 className="text-xl font-bold text-blue-400 mb-3">1. Upload Source Image</h3>
                            <input type="file" accept="image/*" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="w-full bg-slate-700 border-2 border-dashed border-slate-600 rounded-lg p-6 text-center text-slate-400 hover:border-blue-500 hover:text-blue-400 transition-colors"
                            >
                                <UploadIcon className="w-10 h-10 mx-auto mb-2" />
                                {sourceImage ? 'Change Image' : 'Click to Upload'}
                            </button>
                            {sourceImage && (
                                <div className="mt-4 p-2 bg-slate-900 rounded-lg">
                                    <p className="text-xs text-center mb-2 text-slate-400">Source Preview</p>
                                    <img src={sourceImage} alt="Source Preview" className="w-full h-auto rounded" />
                                </div>
                            )}
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-blue-400 mb-3">2. Configure</h3>
                            <label className="flex items-center gap-4">
                                <span className="text-slate-300">Background Color:</span>
                                <input type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} className="w-12 h-8 rounded border-none bg-slate-700 cursor-pointer" />
                            </label>
                        </div>
                        <div>
                            <button onClick={generateIcons} disabled={!sourceImage || isLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg disabled:bg-slate-600 disabled:cursor-not-allowed">
                                {isLoading ? <SpinnerIcon className="w-6 h-6 mx-auto animate-spin-slow" /> : '3. Generate Icons'}
                            </button>
                        </div>
                    </div>

                    {/* Step 3: Preview */}
                    <div className="flex-grow bg-slate-900/50 rounded-lg p-4">
                        <h3 className="text-xl font-bold text-blue-400 mb-4">Preview Generated Icons</h3>
                        {generatedIcons.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                {generatedIcons.map((icon, index) => (
                                    <div key={index} className="text-center">
                                        <div className="bg-slate-700 p-2 rounded-lg inline-block">
                                            <img src={icon.src} alt={`Preview ${icon.size}x${icon.size}`} style={{ width: 96, height: 96 }} className={`rounded-md ${icon.purpose === 'maskable' ? 'rounded-full' : ''}`} />
                                        </div>
                                        <p className="text-xs mt-1 text-slate-300">{icon.size}x{icon.size}</p>
                                        <p className="text-xs text-slate-400 capitalize">{icon.purpose}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center justify-center h-full text-slate-500">
                                <p>Upload an image and click "Generate" to see previews.</p>
                            </div>
                        )}
                    </div>
                </main>

                <footer className="flex-shrink-0 flex items-center justify-end p-4 border-t border-slate-700 gap-4">
                    <button onClick={onClose} className="bg-slate-600 hover:bg-slate-500 text-white font-bold py-2 px-6 rounded-lg transition-colors">
                        Cancel
                    </button>
                    <button onClick={handleSave} disabled={generatedIcons.length === 0 || isLoading} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg transition-colors disabled:bg-slate-600 disabled:cursor-not-allowed flex items-center gap-2">
                        {isLoading ? <SpinnerIcon className="w-5 h-5 animate-spin-slow" /> : <CheckIcon className="w-5 h-5"/>}
                        Save & Apply Icons
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default IconWizard;